"use client"

import React, { useState } from "react"
import { ChevronDown, ChevronUp, Edit, Trash2, Phone, Mail, Building, MapPin } from "lucide-react"
import type { Contact } from "../types"
import ContactNotes from "./ContactNotes"

interface ContactsTableProps {
  contacts: Contact[]
  onEdit: (contact: Contact) => void
  onDelete: (id: string) => void
}

export default function ContactsTable({ contacts, onEdit, onDelete }: ContactsTableProps) {
  const [expandedContact, setExpandedContact] = useState<string | null>(null)

  const toggleExpand = (id: string) => {
    setExpandedContact(expandedContact === id ? null : id)
  }

  return (
    <div className="overflow-x-auto shadow-lg rounded-lg border border-gray-800">
      <table className="min-w-full border-collapse text-sm">
        <thead className="bg-gray-800">
          <tr>
            <th className="p-3 text-left border-b border-gray-700 font-medium text-gray-300 whitespace-nowrap"></th>
            <th className="p-3 text-left border-b border-gray-700 font-medium text-gray-300 whitespace-nowrap">Name</th>
            <th className="p-3 text-left border-b border-gray-700 font-medium text-gray-300 whitespace-nowrap">
              Email
            </th>
            <th className="p-3 text-left border-b border-gray-700 font-medium text-gray-300 whitespace-nowrap">
              Phone
            </th>
            <th className="p-3 text-left border-b border-gray-700 font-medium text-gray-300 whitespace-nowrap">Type</th>
            <th className="p-3 text-left border-b border-gray-700 font-medium text-gray-300 whitespace-nowrap">Role</th>
            <th className="p-3 text-left border-b border-gray-700 font-medium text-gray-300 whitespace-nowrap">
              Company
            </th>
            <th className="p-3 text-left border-b border-gray-700 font-medium text-gray-300 whitespace-nowrap">
              Location
            </th>
            <th className="p-3 text-left border-b border-gray-700 font-medium text-gray-300 whitespace-nowrap">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {contacts.map((contact) => (
            <React.Fragment key={contact._id}>
              <tr className="odd:bg-gray-900 even:bg-gray-850 hover:bg-gray-800 transition-colors">
                <td className="p-3 border-b border-gray-800">
                  <button
                    onClick={() => toggleExpand(contact._id)}
                    className="p-1 hover:bg-gray-700 rounded-full transition-colors"
                  >
                    {expandedContact === contact._id ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </td>
                <td className="p-3 border-b border-gray-800 font-medium">{contact.name}</td>
                <td className="p-3 border-b border-gray-800">
                  {contact.email && (
                    <div className="flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5 text-gray-400" />
                      <a href={`mailto:${contact.email}`} className="hover:text-blue-400 transition-colors">
                        {contact.email}
                      </a>
                    </div>
                  )}
                </td>
                <td className="p-3 border-b border-gray-800">
                  {contact.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3.5 w-3.5 text-gray-400" />
                      <a href={`tel:${contact.phone}`} className="hover:text-blue-400 transition-colors">
                        {contact.phone}
                      </a>
                    </div>
                  )}
                </td>
                <td className="p-3 border-b border-gray-800">
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      contact.contactKind === "Business"
                        ? "bg-blue-900/50 text-blue-300"
                        : "bg-green-900/50 text-green-300"
                    }`}
                  >
                    {contact.contactKind}
                  </span>
                </td>
                <td className="p-3 border-b border-gray-800">
                  <span className="px-2 py-1 rounded-full text-xs bg-gray-700 text-gray-300">
                    {contact.contactType}
                  </span>
                </td>
                <td className="p-3 border-b border-gray-800">
                  {(typeof contact.companyId === "object" && contact.companyId !== null) ? (
                    <div className="flex items-center gap-1">
                      <Building className="h-3.5 w-3.5 text-gray-400" />
                      <span>{contact.companyId.companyName}</span>
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="p-3 border-b border-gray-800">
                  {(typeof contact.officeId === "object" && contact.officeId !== null) ? (
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 text-gray-400" />
                      <span>
                        {contact.officeId.city}, {contact.officeId.stateProvince}
                      </span>
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="p-3 border-b border-gray-800">
                  <div className="flex gap-2">
                    <button
                      onClick={() => onEdit(contact)}
                      className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                      title="Edit contact"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => onDelete(contact._id)}
                      className="p-1.5 bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                      title="Delete contact"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
              {expandedContact === contact._id && (
                <tr className="bg-gray-850">
                  <td colSpan={9} className="p-0 border-b border-gray-800">
                    <div className="p-4 bg-gray-850">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left column: Custom fields */}
                        <div>
                          <h3 className="text-sm font-medium text-gray-300 mb-2">Custom Fields</h3>
                          {(() => {
                            const raw = contact.custom || {};
                            const customData = raw.custom && Object.keys(raw).length === 1
                              ? raw.custom
                              : raw;
                            if (Object.keys(customData).length > 0) {
                              return (
                                <div className="bg-gray-900 rounded-md p-3 space-y-2">
                                  {Object.entries(customData).map(([key, value]) => (
                                    <div key={key} className="flex justify-between">
                                      <span className="text-gray-400">{key}:</span>
                                      <span className="text-gray-200">{String(value)}</span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            return <p className="text-gray-500 text-sm">No custom fields</p>;
                          })()}

                          {/* Company & Office Details */}
                          {contact.companyId && (
                            <div className="mt-4">
                              <h3 className="text-sm font-medium text-gray-300 mb-2">Company Details</h3>
                              <div className="bg-gray-900 rounded-md p-3">
                                <p className="text-gray-200 font-medium">{contact.companyId.companyName}</p>
                                {contact.officeId && (
                                  <div className="mt-2 space-y-1 text-sm">
                                    <p className="text-gray-400">{contact.officeId.officeName}</p>
                                    <p className="text-gray-400">{contact.officeId.address}</p>
                                    <p className="text-gray-400">
                                      {contact.officeId.city}, {contact.officeId.stateProvince},{" "}
                                      {contact.officeId.country}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right column: Notes */}
                        <div>
                          <ContactNotes contactId={contact._id} />
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}
